/**
 * Formatting helpers for values that come from drizzle decimal columns.
 *
 * Drizzle returns `decimal(p, s)` columns as strings (e.g. "4.50", "2.00")
 * to preserve precision. Rendering them raw produces ugly UI like
 * "★ 4.50" or "~2.00 h" — see audit findings #3 / #4 / #15.
 *
 * Use these helpers wherever a decimal column is shown to the user.
 */

type DecimalLike = string | number | null | undefined

function toNumber(value: DecimalLike): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'string' ? parseFloat(value) : value
  return Number.isFinite(n) ? n : null
}

/** "4.50" → "4,5"  ·  null → fallback (default "—") */
export function formatRating(value: DecimalLike, fallback = '—'): string {
  const n = toNumber(value)
  if (n === null) return fallback
  // French formatting (comma as decimal separator)
  return n.toFixed(1).replace('.', ',')
}

/** "2.00" → "2 h"  ·  "2.50" → "2,5 h"  ·  null → "" */
export function formatHours(value: DecimalLike): string {
  const n = toNumber(value)
  if (n === null) return ''
  return Number.isInteger(n) ? `${n} h` : `${n.toFixed(1).replace('.', ',')} h`
}

/** Safe parse for arithmetic — returns 0 instead of NaN. */
export function parseDecimal(value: DecimalLike): number {
  return toNumber(value) ?? 0
}

// French currency formatting (audit #5 — fr-FR uses comma decimals and a
// non-breaking space before €). We hand-roll instead of Intl.NumberFormat
// because Intl emits a regular space + currency symbol which doesn't
// always render as the French standard NBSP and varies by Node ICU build.
const NBSP = '\u00a0'
const NNBSP = '\u202f' // narrow no-break space — French typographic standard

/** 80 → "80 €" · 80.5 → "80,50 €" · 1234 → "1 234 €" · null → "" */
export function formatPrice(value: DecimalLike, opts: { decimals?: 0 | 2 } = {}): string {
  const n = toNumber(value)
  if (n === null) return ''
  const decimals = opts.decimals ?? (Number.isInteger(n) ? 0 : 2)
  const fixed = n.toFixed(decimals)
  const [intPart, fracPart] = fixed.split('.')
  // Thousands separator: narrow NBSP every 3 digits from the right
  const intGrouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP)
  const body = fracPart ? `${intGrouped},${fracPart}` : intGrouped
  return `${body}${NBSP}€`
}

/** 80, 200 → "80 € – 200 €" · null/null → "" · 80,80 → "80 €" */
export function formatPriceRange(min: DecimalLike, max: DecimalLike): string {
  const a = formatPrice(min)
  const b = formatPrice(max)
  if (!a && !b) return ''
  if (!a) return b
  if (!b) return a
  if (a === b) return a
  return `${a}${NBSP}–${NBSP}${b}`
}

// French pluralization helper (audit #24). Rules in French are simpler
// than English: 0 is singular (ø/-e), 1 is singular, 2+ is plural.
// Usage: `${pluralize(count, 'artisan', 'artisans')}` or
//        `${count} ${pluralize(count, 'avis')}` (same form singular/plural).
// For nouns that change irregularly (travail → travaux), pass both forms.
export function pluralize(count: number, singular: string, plural?: string): string {
  if (count < 2) return singular
  return plural ?? `${singular}s`
}

/** "1 artisan" / "0 artisans" → "Aucun artisan" / "2 artisans" */
export function formatCount(count: number, singular: string, plural?: string): string {
  if (count === 0) return `Aucun ${singular}`
  return `${count.toLocaleString('fr-FR').replace(/\s/g, NNBSP)} ${pluralize(count, singular, plural)}`
}
